import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  EmployeeSummary,
  PageResponse,
  SlotApproverView,
} from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  InlineAlert,
  Select,
} from "../../shared/ui";

/**
 * Who may decide release slot bookings on this project.
 *
 * <p>Empty means the project manager, which is the answer for almost every project and needs no
 * configuration to be true. Naming anybody here <b>replaces</b> the manager rather than joining
 * them — a list that quietly included somebody it did not show would make this card a lie about
 * who can approve, and that is exactly the question people open it to answer.
 *
 * <p>Only an administrator sees the controls. Everybody else sees the answer, because "who signs
 * this off" is worth knowing whether or not you can change it.
 */
export function SlotApproversCard({ projectId }: { projectId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState("");
  const [error, setError] = useState<string | null>(null);
  const manage = can("RELEASE_SLOT_APPROVER_MANAGE");

  const approvers = useQuery({
    queryKey: ["slot-approvers", projectId],
    queryFn: () =>
      api.get<SlotApproverView[]>(`/api/v1/projects/${projectId}/slot-approvers`),
  });

  const people = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?size=500&sort=fullName,asc",
      ),
    enabled: manage,
  });

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ["slot-approvers", projectId] });
  };

  const add = useMutation({
    mutationFn: (employeeId: string) =>
      api.post(`/api/v1/projects/${projectId}/slot-approvers/${employeeId}`, {}),
    onSuccess: () => {
      setAdding("");
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not add that person."),
  });

  const remove = useMutation({
    mutationFn: (employeeId: string) =>
      api.del(`/api/v1/projects/${projectId}/slot-approvers/${employeeId}`),
    onSuccess: refresh,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "Could not remove that person."),
  });

  const named = approvers.data ?? [];
  const alreadyNamed = new Set(named.map((person) => person.employeeId));

  return (
    <Card>
      <CardHeader
        title="Who approves release slots"
        subtitle="Bookings on this project's release days. With nobody named, it is the project manager."
      />
      <CardBody className="space-y-3">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {named.length === 0 ? (
          <p className="text-sm text-ink-500">
            The project manager decides bookings here.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {named.map((person) => (
              <li
                key={person.employeeId}
                className="flex items-center gap-1.5 rounded-full bg-ink-100 py-1 pl-3 pr-1.5 text-sm text-ink-800"
              >
                {person.fullName}
                {manage && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(person.employeeId)}
                    aria-label={`Remove ${person.fullName}`}
                    className="grid h-5 w-5 place-items-center rounded-full text-ink-500 hover:bg-ink-200 hover:text-ink-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {manage && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-56 flex-1">
              <Select value={adding} onChange={(event) => setAdding(event.target.value)}>
                <option value="">Name somebody instead</option>
                {(people.data?.content ?? [])
                  .filter((person) => !alreadyNamed.has(person.id))
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.fullName}
                    </option>
                  ))}
              </Select>
            </div>
            <Button
              variant="secondary"
              icon={<Plus className="h-4 w-4" />}
              disabled={adding === ""}
              loading={add.isPending}
              onClick={() => add.mutate(adding)}
            >
              Add
            </Button>
          </div>
        )}

        {manage && named.length > 0 && (
          <p className="text-xs text-ink-500">
            Naming people replaces the project manager rather than adding to them. Remove
            everybody to go back to the manager.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
